import { inject, Inject, Injectable, InjectionToken, Optional, REQUEST_CONTEXT } from "@angular/core";
import { EnvService } from "./env.service";
import { SsrService } from "./ssr.service";
import { Cacheable, CacheableMemory } from "cacheable";
import { StorageMap } from "@ngx-pwa/local-storage";
import { map } from "rxjs/operators";
import { Observable, of } from "rxjs";
import pino from "pino";
import { LogginService } from "./logging/logging-provider";

export enum CacheStorageType {
  Transient = 1,//not used currently, should be inplemente dusing ngrx store or in memory?
  Persistent = 2
}

export enum EnvironmentType {
  Client = 1,
  Server = 2
}

// export const CACHE_CONFIG = new InjectionToken<CacheConfig>('CACHE_CONFIG');


export enum CachePreference {
  ClientOnly = 1,
  ServerOnly = 2,
  ClientAndServer = 3,
}

export interface CacheConfig {
  isServerCacheEnabled: boolean;
  isClientCacheEnabled: boolean;
  isApiCacheEnabled: boolean;
  isApiCompressionEnabled: boolean;

  defaultTtl: number;

  staticLookupTtl: number;
  dynamicLookupTtl: number;
  staticConfigTtl: number;
  dynamicConfigTtl: number;
  staticResourceTtl: number;
  dynamicResourceTtl: number;
}

export abstract class CacheBackend {
  abstract get<T>(key: string): Observable<T | null>;
  abstract set<T>(key: string, value: T, ttl?: number): Observable<void>;
  abstract has(key: string): Observable<boolean>;
  abstract clear(key: string): Observable<void>;
  abstract clearAll(): Observable<void>;
  abstract listKeys?(): Observable<string[]>; // Optional method to list all keys
}


interface ClientCacheItem<T> {
  expiresAt: number; // Timestamp when the item will expire
  data: T; // The cached value
}

@Injectable({
  providedIn: 'root'
})
export class ClientPersistentCacheStorage extends CacheBackend {
  private storageMap: StorageMap = null;
  private cacheConfig: CacheConfig = null;

  listKeys?(): Observable<string[]> {
    throw new Error("Method not implemented.");
  }

  has(key: string): Observable<boolean> {
    return this.storageMap.get(key).pipe(map(value => {

      if (value === null || value === undefined) {
        return false; // Key does not exist
      }

      const item = <ClientCacheItem<any>>value;

      return item.expiresAt && item.expiresAt > Date.now();
    }));
    throw new Error("Method not implemented.");
  }
  clear(key: string): Observable<void> {
    throw new Error("Method not implemented.");
  }
  clearAll(): Observable<void> {
    throw new Error("Method not implemented.");
  }
  get<T>(key: string): Observable<T | null> {
    return <Observable<T>>this.storageMap.get(key).pipe(map(value => {
      let result = undefined;

      if (value === null || value === undefined) {
        return result;
      }

      const item = <ClientCacheItem<T>>value;

      if (item.expiresAt && item.expiresAt > Date.now()) {
        result = item.data; // Return the cached value if it hasn't expired
      }

      return result;
    })
    );
  }
  set<T>(key: string, value: T, ttl?: number): Observable<void> {
    const item = <ClientCacheItem<T>>{
      expiresAt: Date.now() + (ttl || this.cacheConfig.defaultTtl),
      data: value // Convert ttl to milliseconds
    };
    return this.storageMap.set(key, item);
  }

  constructor() {
    super();

    this.storageMap = inject(StorageMap);
    this.cacheConfig = inject(EnvService).cacheConfig;
  }

}

// @Injectable({
//   providedIn: 'root'
// })
export class ServerPersistentCacheStorage implements CacheBackend {

  private _logger: pino.Logger;

  get logger() {
    return this._logger;
  }

  listKeys(): Observable<string[]> {
    const keys = [...this.cacheable.keys].map(key => key.toString());
    return of(keys);
  }

  has(key: string): Observable<boolean> {
    // console.log('keys: %o', [ ... this.cacheable.keys ]);
    return of(this.cacheable.has(key));
  }
  clear(key: string): Observable<void> {
    this.cacheable.delete(key);
    return of(undefined);
  }
  clearAll(): Observable<void> {
    // console.log('Clearing all cache');
    this.cacheable.clear();
    return of(undefined);
  }
  get<T>(key: string): Observable<T | null> {
    return of(this.cacheable.get<T>(key));
  }
  set<T>(key: string, value: T, ttl?: number): Observable<void> {
    this.logger.debug('Setting cache for key: %s, ttl: %d', key, ttl);
    this.cacheable.set(key, value, ttl);

    return of(undefined);
  }

  constructor(private cacheable: CacheableMemory, logger: pino.Logger) {
    this._logger = logger.child({ source: 'server-persistent-cache-storage' }, {});   
  }

}

@Injectable({
  providedIn: 'root'
})
export class TransientCacheStorage implements CacheBackend {

  get<T>(key: string): Observable<T | null> {
    throw new Error("Method not implemented.");
  }
  set<T>(key: string, value: T, ttl?: number): Observable<void> {
    throw new Error("Method not implemented.");
  }
  has(key: string): Observable<boolean> {
    throw new Error("Method not implemented.");
  }
  clear(key: string): Observable<void> {
    throw new Error("Method not implemented.");
  }
  clearAll(): Observable<void> {
    throw new Error("Method not implemented.");
  }
  listKeys?(): Observable<string[]> {
    throw new Error("Method not implemented.");
  }

}

export class HrraRequestContext {
  constructor(public internalProtocol: 'http' | 'https', public port: number, public serverCacheStorage: CacheBackend | null = null, public logger: pino.Logger | null = null) {

  }
}

@Injectable({
  providedIn: 'root'
})
export class CacheBackendFactory {
  constructor(
    @Inject(REQUEST_CONTEXT) private request_context: HrraRequestContext,
    private ssrService: SsrService,
    private clientCacheStorage: ClientPersistentCacheStorage,// not available in SSR
    private transientCacheStorage: TransientCacheStorage, // currenty not implemented
    private envService: EnvService,
    private loggingService: LogginService) {
      this._logger = this.loggingService.createLogger('cache-backend-factory');
    }

  private _logger: pino.Logger;

  get logger() {
    return this._logger;
  }

  getCacheStorage(): CacheBackend {
    this.logger.trace('Getting cache storage based on environment and request context');
    if (this.ssrService.isPlatformServer() && this.request_context?.serverCacheStorage !== null && this.request_context?.serverCacheStorage !== undefined) {
      //console.log('Using server cache storage');
      this.logger.trace('Using server cache storage');
      return this.request_context?.serverCacheStorage;
    }
    else if (this.ssrService.isPlatformBrowser() && this.clientCacheStorage !== null) {
      this.logger.trace('Using client cache storage');
      return new ClientPersistentCacheStorage(); // use server cache storage in browser
      return this.clientCacheStorage;
    }
    else {
      this.logger.trace('Using transient cache storage');
      return this.transientCacheStorage; // fallback to transient in memory cache
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class UniversalCacheService {
  private cacheStorage: CacheBackend;
  private _logger: pino.Logger;
  
  get logger(){
    return this._logger;
  }

  constructor(private cacheBackendFactory: CacheBackendFactory, private loggingService: LogginService) {
    this.cacheStorage = this.cacheBackendFactory.getCacheStorage();
    this._logger = this.loggingService.createLogger('universal-cache-service');
  }

  has(key: string): Observable<boolean> {
    return this.cacheStorage.has(key)
  }

  get<T>(key: string): Observable<T | null> {
    return this.cacheStorage.get<T>(key);
  }

  set<T>(key: string, value: T, ttl?: number): Observable<void> {
    this.logger.trace('Using configured cache storage, setting cache for key: %s, ttl: %d', key, ttl);
    return this.cacheStorage.set<T>(key, value, ttl);
  }

  clear(key: string): Observable<void> {
    //TODO Implementation to clear a specific key
    return of(undefined);
  }

  clearAll(): Observable<void> {
    //TODO Implementation to clear a specific key
    return of(undefined)
  }

}
